import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link as MuiLink
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { DrawService } from "../services/draw/DrawService";
import { ProfileKYCService } from "../services/profilekyc/profilekyc";
import { WalletService } from "../services/wallet/wallet";
import { TicketService } from "../services/Ticket/Ticket";
import { API } from "../constant/apiConstant";
import { setUser } from "../Redux/Reducers/appState";
import { savePendingPaystackPayment } from "../utils/pendingPaystackPayment";

const MAX_TICKETS_PER_PURCHASE = 24;
const MAX_TICKETS_MESSAGE =
  "Maximum tickets per purchase is 24";

function parseBooleanFlag(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }

    if (value === 0) {
      return false;
    }
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    if (["true", "1", "yes"].includes(normalizedValue)) {
      return true;
    }

    if (["false", "0", "no", ""].includes(normalizedValue)) {
      return false;
    }
  }

  return null;
}

function hasVerifiedPurchaseAccess(currentUser) {
  if (!currentUser) {
    return false;
  }

  const identityVerified = parseBooleanFlag(currentUser.identity_verified);
  const emailVerified = parseBooleanFlag(currentUser.is_verified) === true;
  const registrationSource = String(currentUser.registration_source || "")
    .trim()
    .toLowerCase();

  if (identityVerified === true) {
    return true;
  }

  if (identityVerified === null) {
    return emailVerified;
  }

  if (!emailVerified) {
    return false;
  }

  return !["test_bypass", "nin_verified"].includes(registrationSource);
}

function getWinnerDisplayText(draw) {
  const minimumWinners = Number(draw?.minimum_winners || 10);
  const displayWinnerCount = Number(draw?.display_winner_count || minimumWinners);
  const expectedWinnerCount = Number(draw?.expected_winner_count || minimumWinners);
  const winnerPrizeAmount = Number(draw?.winner_prize_amount || 0);
  const payoutPool = Number(draw?.prize_pool || 0) * 0.5;
  const minimumThreshold = minimumWinners * winnerPrizeAmount;

  if (!winnerPrizeAmount || payoutPool < minimumThreshold || displayWinnerCount <= minimumWinners) {
    return `MINIMUM OF ${minimumWinners} WINNERS`;
  }

  return `EXPECTED ${expectedWinnerCount} WINNERS`;
}

export default function BuyTicket() {
  const [activeDraws, setActiveDraws] = useState([]);
  const [countdowns, setCountdowns] = useState({});
  const [ticketCounts, setTicketCounts] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshingProfile, setRefreshingProfile] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({});
  const [walletButtonLoading, setWalletButtonLoading] = useState({});
  const [acceptedLegal, setAcceptedLegal] = useState({});
  const [purchaseMessages, setPurchaseMessages] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.appState.isAuthenticated);
  const sessionExpired = useSelector((state) => state.appState.sessionExpired);
  const user = useSelector((state) => state.appState.user);
  const isVerifiedProfile = hasVerifiedPurchaseAccess(user);

  

  useEffect(() => {
    let active=true,running=false,first=true;
    const fetchDraws = async () => {
      if(running)return;running=true;
      try {
        const res = await DrawService.getActiveDraw();
        if(!active)return;

        if (Array.isArray(res)) {
          const receivedAt = Date.now();
          const drawsWithServerClock = res.map((draw) => ({
            ...draw,
            server_clock_offset_ms: draw.server_time
              ? new Date(draw.server_time).getTime() - receivedAt
              : 0
          }));
          setActiveDraws(drawsWithServerClock);

          const initialTickets = {};
          const initialCountdowns = {};
          const initialLoaders = {};
          const initialLegalAcceptance = {};

          drawsWithServerClock.forEach((draw) => {
            initialTickets[draw.id] = 1;
            initialCountdowns[draw.id] = getCountdown(draw.sales_close_at || draw.draw_date, draw.server_clock_offset_ms);
            initialLoaders[draw.id] = false;
            initialLegalAcceptance[draw.id] = false;
          });

          const reset=first;first=false;
          const preserve=(previous,defaults)=>reset?defaults:Object.fromEntries(Object.keys(defaults).map(id=>[id,previous[id]??defaults[id]]));
          setTicketCounts(previous=>preserve(previous,initialTickets));
          setCountdowns(initialCountdowns);
          setButtonLoading(previous=>preserve(previous,initialLoaders));
          setAcceptedLegal(previous=>preserve(previous,initialLegalAcceptance));
        } else { setActiveDraws([]); }
      } catch (error) {
        if(active)setActiveDraws([]);
        console.log("Error fetching draws:", error);
      } finally {running=false;if(active)setLoading(false);}
    };

    fetchDraws();
    const timer=setInterval(fetchDraws,15000);
    return ()=>{active=false;clearInterval(timer);};
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || sessionExpired) {
      return undefined;
    }

    let isMounted = true;

    const refreshProfile = async () => {
      setRefreshingProfile(true);

      try {
        const [profileResponse, walletResponse] = await Promise.all([
          ProfileKYCService.getProfileKYC(),
          WalletService.getBalance()
        ]);

        if (isMounted && profileResponse?.user) {
          dispatch(setUser(profileResponse.user));
        }

        if (isMounted && walletResponse && !walletResponse?.message) {
          setWalletBalance(Number(walletResponse.balance || 0));
        }
      } catch (error) {
        console.log("Error refreshing profile for ticket purchase:", error);
      }

      if (isMounted) {
        setRefreshingProfile(false);
      }
    };

    refreshProfile();

    return () => {
      isMounted = false;
    };
  }, [dispatch, isAuthenticated, sessionExpired]);

  

  const getCountdown = (targetDate, serverClockOffsetMs = 0) => {
    const drawTime = new Date(targetDate).getTime();
    const now = Date.now() + Number(serverClockOffsetMs || 0);
    const diff = drawTime - now;

    if (diff <= 0) return "expired";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  

  useEffect(() => {
    if (!activeDraws.length) return;

    const interval = setInterval(() => {
      const updated = {};

      activeDraws.forEach((draw) => {
        updated[draw.id] = getCountdown(draw.sales_close_at || draw.draw_date, draw.server_clock_offset_ms);
      });

      setCountdowns(updated);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeDraws]);

  

  const increment = (id) => {
    setTicketCounts((prev) => ({
      ...prev,
      [id]: Math.min((prev[id] || 1) + 1, MAX_TICKETS_PER_PURCHASE)
    }));
  };

  

  const decrement = (id) => {
    setTicketCounts((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1
    }));
  };

  

  const handlePayment = async (draw) => {
    const isExpired = draw.sales_closed || countdowns[draw.id] === "expired";

    if (isExpired) return;

    if (!isAuthenticated || sessionExpired) {
      navigate("/login");
      return;
    }

    if (!isVerifiedProfile) {
      alert("Only verified profiles can participate in live draws.");
      return;
    }

    const quantity = ticketCounts[draw.id];

    if (quantity > MAX_TICKETS_PER_PURCHASE) {
      alert(MAX_TICKETS_MESSAGE);
      return;
    }

    const amount = quantity * Number(draw.ticket_price);

    setButtonLoading((prev) => ({
      ...prev,
      [draw.id]: true
    }));

    try {
      const response = await axios.post(
        `${API.BASE_URL}/api/payment/init`,
        {
          amount,
          draw_id: draw.id,
          quantity,
          type: "ticket_purchase"
        },
        {
          withCredentials: true
        }
      );

      if (!response?.data?.authorization_url || !response?.data?.reference) {
        throw new Error("Payment initialization did not return a valid redirect.");
      }

      savePendingPaystackPayment({
        reference: response.data.reference,
        type: "ticket_purchase",
        returnPath: "/account/authmenu/account/tickets"
      });

      window.location.href = response.data.authorization_url;

    } catch (error) {
      console.log("Payment error:", error);
      alert(error?.response?.data?.message || error?.response?.data?.error || "Failed to initialize payment. Try again.");

      setButtonLoading((prev) => ({
        ...prev,
        [draw.id]: false
      }));
    }
  };

  const refreshWalletSnapshot = async () => {
    try {
      const walletResponse = await WalletService.getBalance();

      if (!walletResponse?.message) {
        setWalletBalance(Number(walletResponse.balance || 0));
      }
    } catch (error) {
      console.log("Error refreshing wallet balance for ticket purchase:", error);
    }
  };

  const buildWalletPurchaseRequestId = (drawId, quantity) => {
    if (window?.crypto?.randomUUID) {
      return `wallet-ticket-${drawId}-${quantity}-${window.crypto.randomUUID()}`;
    }

    return `wallet-ticket-${drawId}-${quantity}-${Date.now()}`;
  };

  const handleWalletPurchase = async (draw) => {
    const isExpired = draw.sales_closed || countdowns[draw.id] === "expired";

    if (isExpired) {
      return;
    }

    if (!isAuthenticated || sessionExpired) {
      navigate("/login");
      return;
    }

    if (!isVerifiedProfile) {
      alert("Only verified profiles can participate in live draws.");
      return;
    }

    const quantity = ticketCounts[draw.id];

    if (quantity > MAX_TICKETS_PER_PURCHASE) {
      alert(MAX_TICKETS_MESSAGE);
      return;
    }

    const totalAmount = quantity * Number(draw.ticket_price || 0);

    if (Number(walletBalance || 0) < totalAmount) {
      setPurchaseMessages((prev) => ({
        ...prev,
        [draw.id]: {
          type: "error",
          text: "Insufficient wallet balance for this purchase."
        }
      }));
      return;
    }

    setWalletButtonLoading((prev) => ({
      ...prev,
      [draw.id]: true
    }));

    setPurchaseMessages((prev) => ({
      ...prev,
      [draw.id]: null
    }));

    try {
      const response = await TicketService.purchaseWithWallet({
        draw_id: draw.id,
        quantity,
        client_request_id: buildWalletPurchaseRequestId(draw.id, quantity)
      });

      if (!response?.ok || !response?.status) {
        setPurchaseMessages((prev) => ({
          ...prev,
          [draw.id]: {
            type: "error",
            text: response?.error || response?.message || "Wallet ticket purchase failed."
          }
        }));
        return;
      }

      await refreshWalletSnapshot();

      setPurchaseMessages((prev) => ({
        ...prev,
        [draw.id]: {
          type: "success",
          text: response?.message || "Tickets purchased successfully from wallet."
        }
      }));

      setTimeout(() => {
        navigate("/account/authmenu/account/tickets");
      }, 500);
    } catch (error) {
      console.log("Wallet ticket purchase error:", error);
      setPurchaseMessages((prev) => ({
        ...prev,
        [draw.id]: {
          type: "error",
          text: error?.response?.data?.message || error?.response?.data?.error || error?.message || "Wallet ticket purchase failed."
        }
      }));
    } finally {
      setWalletButtonLoading((prev) => ({
        ...prev,
        [draw.id]: false
      }));
    }
  };

  

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          minHeight: "100vh",
          color: "#fff",
          p: 3,
          width: "100%",
          maxWidth: 420
        }}
      >

        {loading || refreshingProfile ? (
          <Alert severity="info">Loading active draws...</Alert>
        ) : isAuthenticated && !isVerifiedProfile ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Only verified profiles can participate in live draws.
            </Alert>

            <Card
              sx={{
                background: "linear-gradient(180deg, #0b2a4a 0%, #071f36 100%)",
                borderRadius: 1,
                p: 3
              }}
            >
              <Typography variant="h5" mb={2} fontWeight={700} sx={{ color: "#ffcc33" }}>
                Buy Ticket
              </Typography>

              <Typography sx={{ color: "#ffffff", mb: 2 }}>
                Verify your profile before you can access live draw ticket sales.
              </Typography>

              <Button
                variant="contained"
                sx={{
                  bgcolor: "#5fd068",
                  color: "#003300",
                  fontWeight: 700
                }}
                onClick={() => navigate("/account/authmenu/account/profile-kyc")}
              >
                Go to Profile & KYC
              </Button>
            </Card>
          </>
        ) : activeDraws.length === 0 ? (
          <Alert severity="warning">No active draws available.</Alert>
        ) : (
          <>
            <Typography
              variant="h5"
              mb={2}
              fontWeight={700}
              sx={{ color: "#ffcc33" }}
            >
              Buy Ticket
            </Typography>

            {activeDraws.map((draw) => {

              const isExpired = draw.sales_closed || countdowns[draw.id] === "expired";
              const totalAmount = Number(ticketCounts[draw.id] || 0) * Number(draw.ticket_price || 0);
              const hasEnoughWalletBalance = Number(walletBalance || 0) >= totalAmount;

              return (
                <Card
                  key={draw.id}
                  sx={{
                    background:
                      "linear-gradient(180deg, #0b2a4a 0%, #071f36 100%)",
                    borderRadius: 1,
                    p: 3,
                    mb: 3
                  }}
                >

                  

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >

                    <Chip
                      label={`Draw: ${draw.draw_code}`}
                      sx={{
                        bgcolor: "#102f52",
                        color: "#fff",
                        fontWeight: 600
                      }}
                    />

                    {isExpired ? (
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip
                          label="Ticket Sales Closed"
                          sx={{
                            bgcolor: "#ff5252",
                            color: "#fff",
                            fontWeight: 600
                          }}
                        />
                        <Typography variant="caption" sx={{ color: "#ffcdd2" }}>
                          Ticket sales for this draw are closed.
                        </Typography>
                      </Stack>
                    ) : (
                      <Chip
                        label={`Sales close in: ${countdowns[draw.id]}`}
                        sx={{
                          bgcolor: "#102f52",
                          color: "#fff",
                          fontWeight: 600,
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            whiteSpace: "normal",
                            lineHeight: 1.2
                          }
                        }}
                      />
                    )}

                  </Stack>

                  <Typography sx={{ color: "#b0bec5" }}>
                    Ticket Price: ₦{Number(draw.ticket_price || 0).toLocaleString()}
                  </Typography>

                  <Typography sx={{ color: "#b0bec5", mt: 1 }}>
                    Prize Per Winner: ₦{Number(draw.winner_prize_amount || 0).toLocaleString()}
                  </Typography>

                  <Typography sx={{ color: "#ffffff", mt: 1, fontWeight: 700 }}>
                    {getWinnerDisplayText(draw)}
                  </Typography>

                  <Typography sx={{ color: "#90caf9", mt: 1 }}>
                    Live Draw Time: {new Date(draw.draw_date).toLocaleString("en-NG", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true
                    })}
                  </Typography>

                  <FormControlLabel
                    sx={{ mt: 1, alignItems: "flex-start" }}
                    control={
                      <Checkbox
                        checked={Boolean(acceptedLegal[draw.id])}
                        onChange={(e) =>
                          setAcceptedLegal((prev) => ({
                            ...prev,
                            [draw.id]: e.target.checked
                          }))
                        }
                        sx={{ color: "#ffcc33" }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: "#dbe7f3" }}>
                        I agree to the{" "}
                        <MuiLink
                          component={RouterLink}
                          to="/terms-and-conditions"
                          target="_blank"
                          rel="noreferrer"
                          sx={{ color: "#ffcc33" }}
                        >
                          Terms and Conditions
                        </MuiLink>{" "}
                        and{" "}
                        <MuiLink
                          component={RouterLink}
                          to="/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                          sx={{ color: "#ffcc33" }}
                        >
                          Privacy Policy
                        </MuiLink>
                      </Typography>
                    }
                  />

                  

                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 1,
                      border: "1px solid #1e3a5f"
                    }}
                  >

                    <Typography color="#90caf9" fontWeight={600}>
                      Tickets
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mt={1}
                    >

                      <IconButton
                        disabled={isExpired}
                        aria-label={`Decrease tickets for draw ${draw.draw_code}`}
                        onClick={() => decrement(draw.id)}
                        sx={{
                          bgcolor: "#0f2d4d",
                          color: "#fff"
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>

                      <Typography
                        variant="h4"
                        sx={{
                          color: "white",
                          fontWeight: 700
                        }}
                      >
                        {ticketCounts[draw.id]}
                      </Typography>

                      <IconButton
                        disabled={isExpired || ticketCounts[draw.id] >= MAX_TICKETS_PER_PURCHASE}
                        aria-label={`Increase tickets for draw ${draw.draw_code}`}
                        onClick={() => increment(draw.id)}
                        sx={{
                          bgcolor: "#0f2d4d",
                          color: "#fff"
                        }}
                      >
                        <AddIcon />
                      </IconButton>

                    </Stack>

                    <Divider
                      sx={{
                        my: 2,
                        borderColor: "#1e3a5f"
                      }}
                    />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography color="#b0bec5">
                        Total
                      </Typography>

                      <Typography
                        fontWeight={700}
                        sx={{ color: "white" }}
                      >
                        ₦{Number(ticketCounts[draw.id] * Number(draw.ticket_price || 0)).toLocaleString()}
                      </Typography>
                    </Stack>

                    {ticketCounts[draw.id] >= MAX_TICKETS_PER_PURCHASE && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {MAX_TICKETS_MESSAGE}
                      </Alert>
                    )}

                    {purchaseMessages[draw.id]?.text ? (
                      <Alert severity={purchaseMessages[draw.id].type} sx={{ mt: 2 }}>
                        {purchaseMessages[draw.id].text}
                      </Alert>
                    ) : null}

                    {isAuthenticated ? (
                      <Typography sx={{ mt: 2, color: "#90caf9", fontSize: "0.92rem" }}>
                        Wallet Balance: ₦{Number(walletBalance || 0).toLocaleString()}
                      </Typography>
                    ) : null}

                    <Button
                      fullWidth
                      disabled={
                        walletButtonLoading[draw.id] ||
                        isExpired ||
                        !acceptedLegal[draw.id] ||
                        (isAuthenticated && !isVerifiedProfile) ||
                        !isAuthenticated ||
                        !hasEnoughWalletBalance
                      }
                      sx={{
                        mt: 3,
                        py: 1.4,
                        bgcolor: !hasEnoughWalletBalance
                          ? "#7f8c8d"
                          : walletButtonLoading[draw.id]
                            ? "#c6910099"
                            : "#ffb300",
                        color: "#1a1a1a",
                        fontWeight: 700,
                        borderRadius: 3
                      }}
                      onClick={() => handleWalletPurchase(draw)}
                    >
                      {walletButtonLoading[draw.id] ? (
                        <CircularProgress size={26} sx={{ color: "white" }} />
                      ) : !isAuthenticated ? (
                        "Login to Buy From Wallet"
                      ) : !hasEnoughWalletBalance ? (
                        "Insufficient Wallet Balance"
                      ) : (
                        "Buy Ticket From Wallet"
                      )}
                    </Button>

                    <Button
                      fullWidth
                      disabled={buttonLoading[draw.id] || isExpired || !acceptedLegal[draw.id] || (isAuthenticated && !isVerifiedProfile)}
                      sx={{
                        mt: 1.5,
                        py: 1.4,
                        bgcolor: isExpired
                          ? "#777"
                          : buttonLoading[draw.id]
                          ? "#4ac45a99"
                          : "#5fd068",
                        color: "#003300",
                        fontWeight: 700,
                        borderRadius: 3
                      }}
                      onClick={() => handlePayment(draw)}
                    >

                      {isExpired ? (
                        "Ticket Sales Closed"
                      ) : buttonLoading[draw.id] ? (
                        <CircularProgress
                          size={26}
                          sx={{ color: "white" }}
                        />
                      ) : isAuthenticated ? (
                        "Proceed to Payment"
                      ) : (
                        "Login to Continue"
                      )}

                    </Button>

                  </Box>
                </Card>
              );
            })}
          </>
        )}
      </Box>
    </div>
  );
}
